/**
 * 背景音乐(BGM)面板 —— P2-3 第二刀(2026-05-30)从 VideoCreatePage 抽出。
 * 平台预置(Select + 试听)/ 自定义上传(mp3 ≤10MB)二选一,写回 form.bgmId / customBgmObjectKey。
 * 上传逻辑(校验/调用 service)仍在页面,组件只负责呈现 + 触发回调。
 *
 * 2026-05-31 UX 重排:本面板已固定收进「配音与字幕」可折叠面板(bgmSlot),外层标题/边框由
 * 父面板提供,这里不再自带卡片;自定义上传的全宽原生 file input 改为紧凑「上传 mp3」按钮
 * (对齐 FrameUploadField),解决「上传那条太长」。
 */
import { Dispatch, SetStateAction, useRef } from "react";
import { Upload, X } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { pauseOtherAudio } from "@/shared/lib/exclusiveAudio";
import type { FormState } from "../../model/videoCreateTypes";
import type { VideoBgmDto } from "../../api/videoService";

interface Props {
  bgms: VideoBgmDto[];
  bgmTab: "preset" | "custom";
  setBgmTab: (v: "preset" | "custom") => void;
  form: FormState;
  setForm: Dispatch<SetStateAction<FormState>>;
  customBgmFile: File | null;
  customBgmUrl: string | null;
  uploadingBgm: boolean;
  onClearCustomBgm: () => void;
  onCustomBgmUpload: (f: File) => void;
}

export function VideoBgmPanel({
  bgms,
  bgmTab,
  setBgmTab,
  form,
  setForm,
  customBgmFile,
  customBgmUrl,
  uploadingBgm,
  onClearCustomBgm,
  onCustomBgmUpload,
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  return (
    <Tabs value={bgmTab} onValueChange={(v) => setBgmTab(v as "preset" | "custom")}>
      <TabsList className="grid grid-cols-2 w-full h-8">
        <TabsTrigger value="preset" className="text-xs">
          平台预置 ({bgms.length})
        </TabsTrigger>
        <TabsTrigger value="custom" className="text-xs">
          自定义上传
        </TabsTrigger>
      </TabsList>

      <TabsContent value="preset" className="space-y-2 mt-2">
        <Select
          value={form.bgmId ? String(form.bgmId) : "none"}
          onValueChange={(v) => {
            onClearCustomBgm();
            setForm((s) => ({
              ...s,
              customBgmObjectKey: null,
              bgmId: v === "none" ? null : Number(v),
            }));
          }}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder="选择 BGM" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">无 BGM</SelectItem>
            {bgms.map((b) => (
              <SelectItem key={b.id} value={String(b.id)}>
                {b.name} · {b.durationSec}s
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {form.bgmId && bgms.find((b) => b.id === form.bgmId)?.url && (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <audio
            onPlay={pauseOtherAudio}
            src={bgms.find((b) => b.id === form.bgmId)!.url}
            controls
            preload="none"
            className="h-8 w-full"
            aria-label="试听 BGM"
          />
        )}
      </TabsContent>

      <TabsContent value="custom" className="mt-2">
        <input
          ref={fileRef}
          type="file"
          accept="audio/mpeg,audio/mp3"
          className="hidden"
          disabled={uploadingBgm}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onCustomBgmUpload(f);
            e.target.value = "";
          }}
        />
        {!form.customBgmObjectKey ? (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 shrink-0 gap-1.5"
              disabled={uploadingBgm}
              onClick={() => fileRef.current?.click()}
            >
              <Upload className="h-3.5 w-3.5" />
              {uploadingBgm ? "上传中…" : "上传 mp3"}
            </Button>
            <span className="text-2xs text-muted-foreground">mp3 ≤10MB · 自行确保版权合规</span>
          </div>
        ) : (
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 rounded-md border bg-background/40 px-2.5 py-1.5">
              <span className="min-w-0 flex-1 truncate text-xs" title={customBgmFile?.name}>
                {customBgmFile?.name ?? "自传 mp3"}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
                aria-label="移除自传 BGM"
                onClick={() => {
                  onClearCustomBgm();
                  setForm((s) => ({ ...s, customBgmObjectKey: null }));
                }}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
            {customBgmUrl && (
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <audio
                onPlay={pauseOtherAudio}
                src={customBgmUrl}
                controls
                preload="none"
                className="h-8 w-full"
                aria-label="试听自传 BGM"
              />
            )}
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
}
